import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import PolicySection from '../privacy-policy/components/PolicySection';
import TableOfContents from '../privacy-policy/components/TableOfContents';
import Footer from '../homepage-sales-landing/components/Footer';

const CookiePolicy = () => {
  const [activeSection, setActiveSection] = useState('introduction');
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: true,
    marketing: false,
    thirdParty: false
  });

  const handlePreferenceChange = (category) => {
    if (category === 'essential') return; // Essential cookies cannot be disabled
    
    setCookiePreferences(prev => ({
      ...prev,
      [category]: !prev?.[category]
    }));
  };

  const handleSavePreferences = () => {
    // In a real implementation, this would save to localStorage and update cookies
    alert('Cookie preferences saved successfully!');
  };

  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: 'Cookie',
      content: (
        <>
          <p className="mb-4">
            This Cookie Policy explains how AuthorPreneur AI, operated by Entrepreneur Power Network, LLC, uses cookies and similar tracking technologies when you visit our platform. This policy provides comprehensive information about cookie usage, tracking technologies, and your privacy choices.
          </p>
          <p className="mb-4">
            By using AuthorPreneur AI, you consent to the use of cookies in accordance with this policy. We are committed to transparency in our data collection practices and provide you with granular controls over your cookie preferences.
          </p>
          <p>
            Last Updated: December 8, 2025
          </p>
        </>
      )
    },
    {
      id: 'what-are-cookies',
      title: 'What Are Cookies?',
      icon: 'Info',
      content: (
        <>
          <p className="mb-4">
            Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
          </p>
          
          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">How Cookies Work</h3>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Cookies store information about your visit and preferences</li>
            <li>They help us recognize you when you return to our platform</li>
            <li>Cookies improve your browsing experience by remembering your settings</li>
            <li>They enable certain platform features and functionality</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3">Types of Cookies by Duration</h3>
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Session Cookies</h4>
              <p className="text-sm text-muted-foreground">
                Temporary cookies that expire when you close your browser. Used for essential platform functionality during your session.
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Persistent Cookies</h4>
              <p className="text-sm text-muted-foreground">
                Remain on your device for a set period or until you delete them. Used to remember your preferences across sessions.
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'cookie-categories',
      title: 'Cookie Categories',
      icon: 'Layers',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-4">1. Essential Cookies (Required)</h3>
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  These cookies are necessary for the platform to function and cannot be disabled. They enable core functionality such as:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>User authentication and session management</li>
                  <li>Security and fraud prevention</li>
                  <li>Load balancing and system availability</li>
                  <li>Shopping cart functionality (if applicable)</li>
                  <li>Form submission and error validation</li>
                </ul>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-4 mt-8">2. Analytics Cookies (Optional)</h3>
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <p className="text-sm text-muted-foreground mb-3">
              Help us understand how users interact with our platform by collecting anonymous usage data:
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
              <li>Page views and navigation patterns</li>
              <li>Time spent on pages and features</li>
              <li>Click-through rates and user flow</li>
              <li>Device and browser information</li>
              <li>Geographic location (approximate)</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-3">
              <strong>Retention Period:</strong> 26 months (Google Analytics standard)
            </p>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-4 mt-8">3. Marketing Cookies (Optional)</h3>
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <p className="text-sm text-muted-foreground mb-3">
              Used to deliver personalized advertisements and measure campaign effectiveness:
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
              <li>Track advertising campaign performance</li>
              <li>Display relevant ads based on interests</li>
              <li>Limit ad frequency to avoid repetition</li>
              <li>Measure conversion rates and ROI</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-3">
              <strong>Retention Period:</strong> Varies by provider (typically 13-24 months)
            </p>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-4 mt-8">4. Third-Party Integration Cookies</h3>
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-3">
              Cookies from external services integrated into our platform:
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
              <li>Payment processing (Stripe)</li>
              <li>AI services (ChatGPT API)</li>
              <li>Community platform (Skool)</li>
              <li>Social media integrations</li>
            </ul>
          </div>
        </>
      )
    },
    {
      id: 'specific-services',
      title: 'Specific Service Cookies',
      icon: 'Server',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-4">Stripe Payment Processing</h3>
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Purpose</h4>
                <p className="text-sm text-muted-foreground">
                  Secure payment processing, fraud detection, and transaction management
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Cookies Used</h4>
                <p className="text-sm text-muted-foreground">
                  __stripe_mid, __stripe_sid, stripe_js_ver
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Retention</h4>
                <p className="text-sm text-muted-foreground">
                  Session cookies (expire on browser close) and 1-year persistent cookies
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Data Shared</h4>
                <p className="text-sm text-muted-foreground">
                  Payment information, transaction details (processed securely according to PCI DSS standards)
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-4 mt-8">ChatGPT API (OpenAI)</h3>
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Purpose</h4>
                <p className="text-sm text-muted-foreground">
                  AI-powered book writing assistance, content generation, and manuscript processing
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Session Management</h4>
                <p className="text-sm text-muted-foreground">
                  API session tokens for maintaining conversation context and AI interactions
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Retention</h4>
                <p className="text-sm text-muted-foreground">
                  Session cookies (cleared after 30 minutes of inactivity)
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Data Processing</h4>
                <p className="text-sm text-muted-foreground">
                  Your content is processed through OpenAI's API. See OpenAI's privacy policy for details on data handling
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-4 mt-8">Skool Community Integration</h3>
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Purpose</h4>
                <p className="text-sm text-muted-foreground">
                  Community access management, membership verification, and social features
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Tracking</h4>
                <p className="text-sm text-muted-foreground">
                  Community participation, engagement metrics, and membership status
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Retention</h4>
                <p className="text-sm text-muted-foreground">
                  Persistent cookies (duration set by Skool platform)
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-4 mt-8">Google Analytics</h3>
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Purpose</h4>
                <p className="text-sm text-muted-foreground">
                  Website analytics, user behavior analysis, and performance monitoring
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Cookies Used</h4>
                <p className="text-sm text-muted-foreground">
                  _ga, _gid, _gat, _ga_&lt;container-id&gt;
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Retention</h4>
                <p className="text-sm text-muted-foreground">
                  _ga: 2 years, _gid: 24 hours, _gat: 1 minute
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Data Collected</h4>
                <p className="text-sm text-muted-foreground">
                  Anonymous usage statistics, page views, session duration, bounce rates
                </p>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'cookie-preferences',
      title: 'Cookie Preference Center',
      icon: 'Settings',
      content: (
        <>
          <p className="mb-6">
            Manage your cookie preferences below. Essential cookies cannot be disabled as they are necessary for platform functionality.
          </p>

          <div className="space-y-4">
            {/* Essential Cookies */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                    <h4 className="font-semibold text-foreground">Essential Cookies</h4>
                    <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                      Always Active
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Required for basic platform functionality, authentication, and security. Cannot be disabled.
                  </p>
                </div>
                <div className="ml-4">
                  <div className="w-12 h-6 bg-primary rounded-full relative">
                    <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="BarChart2" size={20} color="var(--color-primary)" />
                    <h4 className="font-semibold text-foreground">Analytics Cookies</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Help us improve the platform by collecting anonymous usage statistics and performance metrics.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => handlePreferenceChange('analytics')}
                    className={`w-12 h-6 rounded-full relative transition-colors ${
                      cookiePreferences?.analytics ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        cookiePreferences?.analytics ? 'right-1' : 'left-1'
                      }`}
                    ></div>
                  </button>
                </div>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Target" size={20} color="var(--color-primary)" />
                    <h4 className="font-semibold text-foreground">Marketing Cookies</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Enable personalized advertisements and measure campaign effectiveness.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => handlePreferenceChange('marketing')}
                    className={`w-12 h-6 rounded-full relative transition-colors ${
                      cookiePreferences?.marketing ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        cookiePreferences?.marketing ? 'right-1' : 'left-1'
                      }`}
                    ></div>
                  </button>
                </div>
              </div>
            </div>

            {/* Third-Party Cookies */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Link" size={20} color="var(--color-primary)" />
                    <h4 className="font-semibold text-foreground">Third-Party Integration Cookies</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Enable features from external services like Stripe, ChatGPT, and community integrations.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => handlePreferenceChange('thirdParty')}
                    className={`w-12 h-6 rounded-full relative transition-colors ${
                      cookiePreferences?.thirdParty ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        cookiePreferences?.thirdParty ? 'right-1' : 'left-1'
                      }`}
                    ></div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={handleSavePreferences}
              className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors font-medium"
            >
              Save Cookie Preferences
            </button>
          </div>
        </>
      )
    },
    {
      id: 'browser-settings',
      title: 'Browser Cookie Controls',
      icon: 'Globe',
      content: (
        <>
          <p className="mb-6">
            You can control and manage cookies through your browser settings. Most browsers allow you to block or delete cookies. However, blocking all cookies may affect platform functionality.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-4">Browser-Specific Instructions</h3>
          
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2 flex items-center">
                <Icon name="Chrome" size={18} className="mr-2" />
                Google Chrome
              </h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Click the three-dot menu → Settings</li>
                <li>Navigate to Privacy and Security → Cookies and site data</li>
                <li>Configure your cookie preferences</li>
              </ol>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2 flex items-center">
                <Icon name="Firefox" size={18} className="mr-2" />
                Mozilla Firefox
              </h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Click menu button → Settings</li>
                <li>Select Privacy & Security</li>
                <li>Under Cookies and Site Data, adjust settings</li>
              </ol>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2 flex items-center">
                <Icon name="Safari" size={18} className="mr-2" />
                Safari
              </h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Open Safari → Preferences</li>
                <li>Click Privacy tab</li>
                <li>Manage cookie preferences and website data</li>
              </ol>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2 flex items-center">
                <Icon name="Edge" size={18} className="mr-2" />
                Microsoft Edge
              </h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Click three-dot menu → Settings</li>
                <li>Select Cookies and site permissions</li>
                <li>Manage cookies and stored data</li>
              </ol>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mt-6">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Important:</strong> Blocking or deleting cookies may prevent certain features from working properly, such as staying logged in or saving your preferences.
              </p>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-4 mt-8">GDPR & CCPA Compliance</h3>
          
          <div className="space-y-6">
            <div className="bg-muted/50 rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">GDPR Cookie Consent</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Under the General Data Protection Regulation (GDPR), we require explicit consent for non-essential cookies. You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                <li>Accept or reject non-essential cookies at any time</li>
                <li>Withdraw consent previously given</li>
                <li>Access information about cookies we use</li>
                <li>Request deletion of data collected through cookies</li>
              </ul>
            </div>

            <div className="bg-muted/50 rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">CCPA Tracking Disclosures</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Under the California Consumer Privacy Act (CCPA), California residents have additional rights regarding cookie tracking:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                <li>Right to know what personal information is collected through cookies</li>
                <li>Right to opt-out of the "sale" of personal information</li>
                <li>Right to request deletion of cookie-collected data</li>
                <li>Right to non-discrimination for exercising privacy rights</li>
              </ul>
            </div>

            <div className="bg-muted/50 rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Do Not Track Signals</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Some browsers include a "Do Not Track" (DNT) feature. We honor DNT signals for analytics and marketing cookies, though essential cookies remain active for platform functionality.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">To enable DNT:</strong> Check your browser's privacy settings and enable the "Do Not Track" option.
              </p>
            </div>
          </div>
          
          <div className="bg-muted/50 border border-border rounded-lg p-6 mt-6">
            <div className="flex items-start space-x-4">
              <Icon name="Shield" size={24} color="var(--color-primary)" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">GDPR & Data Protection</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  For comprehensive information about GDPR compliance and your data protection rights, please visit our dedicated GDPR Compliance page.
                </p>
                <Link 
                  to="/gdpr-compliance"
                  className="inline-flex items-center text-primary hover:underline text-sm"
                >
                  View GDPR Compliance
                  <Icon name="ArrowRight" size={16} className="ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'cookie-audit',title: 'Cookie Audit Log',icon: 'FileText',
      content: (
        <>
          <p className="mb-6">
            You can view a detailed audit of all cookies currently active on your device from our domain. This transparency feature allows you to understand exactly what data is being stored.
          </p>

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="bg-muted px-6 py-3 border-b border-border">
              <h4 className="font-semibold text-foreground">Current Active Cookies</h4>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-start pb-4 border-b border-border">
                  <div className="flex-1">
                    <p className="font-medium text-foreground mb-1">session_token</p>
                    <p className="text-sm text-muted-foreground">Type: Essential | Duration: Session</p>
                  </div>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                    Active
                  </span>
                </div>
                <div className="flex justify-between items-start pb-4 border-b border-border">
                  <div className="flex-1">
                    <p className="font-medium text-foreground mb-1">user_preferences</p>
                    <p className="text-sm text-muted-foreground">Type: Essential | Duration: 1 year</p>
                  </div>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                    Active
                  </span>
                </div>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-foreground mb-1">_ga</p>
                    <p className="text-sm text-muted-foreground">Type: Analytics | Duration: 2 years</p>
                  </div>
                  <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button className="w-full border border-border text-foreground px-6 py-3 rounded-lg hover:bg-muted transition-colors font-medium">
              Download Complete Cookie Report
            </button>
          </div>
        </>
      )
    },
    {
      id: 'policy-updates',title: 'Policy Updates & Notifications',icon: 'Bell',
      content: (
        <>
          <p className="mb-4">
            We may update this Cookie Policy periodically to reflect changes in our practices, technologies, or legal requirements. When significant changes are made:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Users will be notified via email or platform notification</li>
            <li>The "Last Updated" date will be revised at the top of this page</li>
            <li>A summary of key changes will be provided</li>
            <li>Users may need to re-consent to updated cookie practices</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-4 mt-8">Version History</h3>
          <div className="space-y-3">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-foreground">Version 1.0</h4>
                <span className="text-sm text-muted-foreground">December 8, 2025</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Initial Cookie Policy publication covering all service integrations and compliance requirements.
              </p>
            </div>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-6 mt-6">
            <div className="flex items-start space-x-4">
              <Icon name="Mail" size={24} color="var(--color-primary)" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">Stay Informed</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Subscribe to policy update notifications to receive alerts when our Cookie Policy changes.
                </p>
                <button className="text-sm text-primary hover:underline">
                  Manage Notification Preferences →
                </button>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'contact',title: 'Contact & Support',icon: 'HelpCircle',
      content: (
        <>
          <p className="mb-6">
            If you have questions about our Cookie Policy or need assistance managing your cookie preferences, please contact us:
          </p>

          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <div>
              <h4 className="font-semibold text-foreground mb-2">Email Support</h4>
              <a
                href="mailto:success@entrepreneurpowernetwork.com"
                className="text-primary hover:underline"
              >
                success@entrepreneurpowernetwork.com
              </a>
              <p className="text-sm text-muted-foreground mt-1">
                Response time: Within 48 hours
              </p>
            </div>

            <div className="border-t border-border pt-4">
              <h4 className="font-semibold text-foreground mb-2">Mailing Address</h4>
              <p className="text-muted-foreground">
                Entrepreneur Power Network, LLC<br />
                813 N. Main Street, Suite 502<br />
                McAllen, TX 78501<br />
                United States
              </p>
            </div>

            <div className="border-t border-border pt-4">
              <h4 className="font-semibold text-foreground mb-2">Data Protection Inquiries</h4>
              <p className="text-sm text-muted-foreground">
                For GDPR, CCPA, or other privacy-related questions, contact our Data Protection Officer at the email address above with "Privacy Inquiry" in the subject line.
              </p>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <Link
              to="/privacy-policy"
              className="flex items-center justify-between bg-muted/50 border border-border rounded-lg p-4 hover:bg-muted transition-colors"
            >
              <div>
                <h5 className="font-medium text-foreground mb-1">Privacy Policy</h5>
                <p className="text-xs text-muted-foreground">Learn about data protection</p>
              </div>
              <Icon name="ArrowRight" size={20} color="var(--color-muted-foreground)" />
            </Link>
            <Link
              to="/terms-of-service"
              className="flex items-center justify-between bg-muted/50 border border-border rounded-lg p-4 hover:bg-muted transition-colors"
            >
              <div>
                <h5 className="font-medium text-foreground mb-1">Terms of Service</h5>
                <p className="text-xs text-muted-foreground">Review platform terms</p>
              </div>
              <Icon name="ArrowRight" size={20} color="var(--color-muted-foreground)" />
            </Link>
          </div>
        </>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Cookie Policy - AuthorPreneur AI</title>
        <meta
          name="description"
          content="Understand how AuthorPreneur AI uses cookies and tracking technologies. Manage your cookie preferences and learn about your privacy choices."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="BookOpen" size={24} color="var(--color-primary)" />
                </div>
                <span className="text-xl font-heading font-semibold text-foreground">
                  AuthorPreneur AI
                </span>
              </Link>
              <Link
                to="/legal-pages-hub"
                className="text-sm text-muted-foreground hover:text-primary flex items-center"
              >
                <Icon name="ArrowLeft" size={16} className="mr-1" />
                Back to Legal Hub
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            {/* Page Header */}
            <div className="mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 dark:bg-orange-900/20 rounded-2xl mb-6">
                <Icon name="Cookie" size={32} color="var(--color-warning)" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                Cookie Policy
              </h1>
              <p className="text-lg text-muted-foreground mb-2">
                Last Updated: December 8, 2025
              </p>
              <p className="text-muted-foreground">
                Effective Date: December 8, 2025
              </p>
            </div>

            <div className="grid lg:grid-cols-4 gap-8">
              {/* Table of Contents - Sidebar */}
              <div className="lg:col-span-1">
                <TableOfContents
                  sections={sections}
                  activeSection={activeSection}
                  onSectionClick={setActiveSection}
                />
              </div>

              {/* Policy Content */}
              <div className="lg:col-span-3 space-y-8">
                {sections?.map((section) => (
                  <PolicySection
                    key={section?.id}
                    section={section}
                    isActive={activeSection === section?.id}
                  />
                ))}

                {/* Quick Actions */}
                <div className="bg-muted/50 border border-border rounded-xl p-6 mt-12">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Quick Actions
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <button className="flex items-center space-x-3 bg-card border border-border p-4 rounded-lg hover:bg-muted transition-colors">
                      <Icon name="Download" size={20} color="var(--color-primary)" />
                      <span className="text-sm font-medium text-foreground">Download PDF</span>
                    </button>
                    <button className="flex items-center space-x-3 bg-card border border-border p-4 rounded-lg hover:bg-muted transition-colors">
                      <Icon name="Mail" size={20} color="var(--color-primary)" />
                      <span className="text-sm font-medium text-foreground">Email Copy</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CookiePolicy;